import React, { useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { overallHeatMap } from "../services/api-services";
import { toastEmitter } from "../utills/utilities";
import ZoomModal from "./heatmap/zoomModal";
import Modal from "../admin/components/common/Model";
import Remark from "../admin/components/Charts/Remark";
import { CHAT_ICON, MENU_ICON, ZOOM_ICON } from "../utills/image-constant";

const WeightedGapChart = ({ selectedCompanyId,selectedCampaignId,type, graphName, title = "Weighted Gap Chart" }) => {
  const [remark, setRemark] = useState("");
  const [remarkId, setRemarkId] = useState(null);
  const [isAttempt, setIsAttempt] = useState("");
  const [responseBody, setResponseBody] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTextBox, setShowTextBox] = useState(false);
  const [note, setNote] = useState("");
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [activeDimension, setActiveDimension] = useState(null);
  const modalRef = useRef(null);

  const modalId = `zoomModal-${title.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

  const getOverAllHeatMapData = async () => {
    setLoading(true);
    try {
      const response = await overallHeatMap(selectedCompanyId,selectedCampaignId,type, graphName);
      if (response?.data?.responseCode === 200) {
        const remark = response?.data?.remark || "";
        const remarkId = response?.data?.remarkId || null;
        const isAttempt = response?.data?.isAttempt ?? 0;

        setRemarkText(remark);
        setRemarkId(remarkId);
        setIsAttempt(isAttempt);
        setResponseBody(response?.data?.responseBody || []);
      } else if (response?.data?.responseCode === 404) {
        setResponseBody([]);
      } else {
        toastEmitter("error", response?.data?.message);
      }
    } catch (err) {
      toastEmitter("error", "Service unavailable.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) getOverAllHeatMapData();
  }, [selectedCompanyId]);

  const getChartData = () => {
    const subDimensions = [];
    const seriesData = [];

    let allSubDimensions = [];
    responseBody.forEach((dimension) => {
      dimension.subDimensionOverallHeatmapChartResponseList?.forEach((subDimension) => {
        allSubDimensions.push({
          name: subDimension.subDimensionName,
          percentage: subDimension.percentage,
        });
      });
    });

    allSubDimensions = allSubDimensions.sort((a, b) => b.percentage - a.percentage);

    allSubDimensions.forEach((subDimension) => {
      subDimensions.push(subDimension.name);
      seriesData.push(subDimension.percentage?.toFixed(2));
    });

    return {
      categories: subDimensions,
      series: [
        {
          name: "Sub-Dimensions Percentages",
          data: seriesData,
        },
      ],
    };
  };

  const chartData = getChartData();

 const options = {
  chart: {
    id: "gap_chart",
    type: "bar",
    height: 350
  },
  plotOptions: {
    bar: {
      horizontal: true,
      barHeight: "45%",
      dataLabels: {
        position: "top", // 'top' works for horizontal bars as end
      },
    },
  },
  dataLabels: {
    enabled: true,
    formatter: (val) => `${val}%`,
    offsetX: 15,
    style: {
      colors: ["#000"],
      fontSize: "12px",
      fontWeight: "bold",
    },
  },
  xaxis: {
    categories: chartData.categories,
    min: 0,
    max: 60, // set max value
  },
  tooltip: {
    y: {
      formatter: (val) => `${val}%`
    }
  },
  fill: {
    opacity: 1
  }
};


  const exportAsExcel = () => {
    const titleRow = [[title]];
    const headers = ["Dimension/Subdimension Name", "Percentage"];
    const rows = [];

    responseBody.forEach((dimension) => {
      rows.push([dimension.dimensionName, dimension.percentage?.toFixed(2)]);
      dimension.subDimensionOverallHeatmapChartResponseList?.forEach((sub) => {
        rows.push([`  - ${sub.subDimensionName}`, sub.percentage?.toFixed(2)]);
      });
    });

    const sheetData = [...titleRow, [], headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${title}.xlsx`);
  };

 // PDF Export
  const exportAsPDF = async () => {
    const chartBox = document.getElementById(`chart-box-${title}`);
    if (!chartBox) return;
    chartBox.scrollTo(0, 0);
    await new Promise((r) => setTimeout(r, 200));

    const chartElement = chartBox.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth(); // Width of the PDF
    const pdfHeight = pdf.internal.pageSize.getHeight(); // Height of the PDF

    const canvasWidth = canvas.width; // Width of the canvas image
    const canvasHeight = canvas.height; // Height of the canvas image

    // Scaling factor to make the image a bit smaller (e.g., scale down by 80%)
    const scaleFactor =
      Math.min(pdfWidth / canvasWidth, pdfHeight / canvasHeight) * 0.8; // 0.8 reduces the size to 80%

    const imgWidth = canvasWidth * scaleFactor; // Scaled width
    const imgHeight = canvasHeight * scaleFactor; // Scaled height

    // Calculate the offset to center the image
    const xOffset = (pdfWidth - imgWidth) / 2;
    const yOffset = (pdfHeight - imgHeight) / 2;

    // Add the image to the PDF, centered and scaled down
    pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);

    // Save the PDF
    pdf.save(`${title || "chart"}.pdf`);
  };

const hideDropdownBeforeExport = () => {
    const dropdown = document.querySelector(".dropdown-menu.show");
    if (dropdown) dropdown.classList.remove("show");
  };
 const exportAsImage = async () => {
    hideDropdownBeforeExport();
    const chartBox = document.getElementById(`chart-box-${title}`);
    if (!chartBox) return;
    chartBox.scrollTo(0, 0);
    await new Promise(r => setTimeout(r, 200));

    const chartElement = chartBox.querySelector(".apexcharts-canvas");
    if (!chartElement) return;

    const canvas = await html2canvas(chartElement, {
      scale: 2,
      backgroundColor: "#fff",
      useCORS: true,
    });

    const link = document.createElement("a");
    link.download = `${title || "chart"}.png`;
    link.href = canvas.toDataURL();
    link.click();
  };
  return (
    <div id={`chart-box-${title}`} className="card h-100">
      {/* Toolbar */}
      <div className="d-flex justify-content-between align-items-center overall-text mb-3 p-2">
        <h5>{title}</h5>
        <div className="d-flex align-items-center">
          <button className="border-0 bg-transparent me-2" data-bs-toggle="modal" data-bs-target={`#${modalId}`}>
            <img src={ZOOM_ICON} alt="zoom" />
          </button>
          <div className="dropdown">
            <button className="icon-btn bg-transparent" type="button" data-bs-toggle="dropdown">
              <img src={MENU_ICON} alt="menu" />
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li><button className="dropdown-item" onClick={exportAsExcel}>Download Excel</button></li>
              <li><button className="dropdown-item" onClick={exportAsPDF}>Export PDF</button></li>
              <li><button className="dropdown-item" onClick={exportAsImage}>Download Image</button></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Chart */}
    {loading ? (
          <div className="loader-container"><div className="loader"></div></div>
        ) : (
           <Chart options={options} series={chartData.series} type="bar" height={350} />
        )}
      <div className="d-flex justify-content-end mb-2">
        <button
          title="Add Comment"
          onClick={() => {
            setShowRemarkModal(true);
            setTimeout(() => {
              if (modalRef.current) modalRef.current.click();
            }, 0);
          }}
          className="border-0 bg-transparent position-absolute"
          style={{
            bottom: "10px",
            right: "10px",
          }}
        >
          <img src={CHAT_ICON} alt="chat" />
        </button>
      </div>
      {/* Remark Modal */}
      {showRemarkModal && (
        <Modal modalClick={modalRef} title="Remark" buttonLable="">
          <Remark
          selectedCompanyId={selectedCompanyId}
            selectedCampaignId={selectedCampaignId}
            type={type}
            graphName={graphName}
            initialRemark={remarkText}
            remarkId={remarkId}
            isAttempt={0}
            setRemarkprops={setRemarkText}
            setRemarkIdProps={setRemarkId}
            onCloseModal={() => setShowRemarkModal(false)}
          />
        </Modal>
      )}


      {/* Zoom Modal */}
      <ZoomModal
        id={`zoomModal-${title.replace(/[^a-zA-Z0-9_-]/g, "_")}`}
        title={title}
        chartOptions={options}
        seriesData={chartData.series}
        height={400}
        remarkText={remarkText}
        setRemarkText={setRemarkText}
        showRemarkModal={showRemarkModal}
        setShowRemarkModal={setShowRemarkModal}
        modalRef={modalRef}
        exportSummaryToExcel={exportAsExcel}
        exportAsPDF={exportAsPDF}
        exportAsImage={exportAsImage}
      />

    </div>
  );
};

export default WeightedGapChart;
